import React, { useEffect } from 'react';
import { Project } from '../types';
import { X, ExternalLink, AlertCircle } from 'lucide-react';

interface ProjectModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ project, isOpen, onClose }) => {
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Content */}
      <div className="relative w-full h-[90vh] max-w-[90vw] bg-surface border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up z-50">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-surface/50 backdrop-blur-md shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-white truncate max-w-[200px] sm:max-w-md">{project.title}</h2>
            <a 
              href={project.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-medium bg-primary/20 text-primary rounded-full hover:bg-primary/30 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              <ExternalLink size={12} />
              Open in new tab
            </a>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Preview Area (Scrollable iframe container) */}
        <div className="flex-1 w-full bg-white relative overflow-hidden">
            {/* Fallback/Warning for X-Frame-Options */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 z-0 bg-slate-100">
                <AlertCircle size={40} className="mb-2 opacity-50" />
                <p>Loading Preview...</p>
                <p className="text-xs text-center px-4 mt-2 max-w-md">
                   If the website does not load, it might be blocking embedded previews (iFrames). 
                   Please use the "Open in new tab" button.
                </p>
            </div>
            
            <iframe 
                src={project.url}
                title={project.title}
                className="w-full h-full relative z-10 bg-white"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
        </div>
        
        {/* Footer info */}
        <div className="p-4 bg-surface border-t border-white/5 text-sm text-slate-400 flex justify-between items-center shrink-0">
             <span>Built by <strong>IQODEX</strong></span>
             <span className="flex gap-2">
                 {project.tags.map(t => <span key={t}>#{t}</span>)}
             </span>
        </div>
      </div>
    </div>
  );
};